import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { analyticsApi } from '@/api';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ErrorState,
  LoadingState,
  PageHeader,
  Skeleton,
  StatTile,
} from '@/components/ui';
import { ExportMenu } from '@/features/analytics/ExportMenu';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { ReportTable } from '@/features/analytics/ReportTable';
import { TrendChart } from '@/features/analytics/TrendChart';
import { count, days, percent } from '@/features/analytics/format';
import { REVENUE_SERIES, SUBSCRIPTION_SERIES } from '@/features/analytics/series';
import { formatMoney } from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';

const EXPORT_SHEETS = [
  { key: 'revenue-summary', label: 'Revenue summary', hint: 'One row: billed, collected, owed' },
  { key: 'revenue-trend', label: 'Revenue over time', hint: 'Billed and collected per bucket' },
  { key: 'revenue-plans', label: 'Revenue by plan', hint: 'Grouped by the plan on each invoice' },
  {
    key: 'revenue-coupons',
    label: 'What discounts cost',
    hint: 'Per code, read from the invoices',
  },
  {
    key: 'subscriptions-summary',
    label: 'Subscription summary',
    hint: 'Churn, net new, who is leaving',
  },
  {
    key: 'subscriptions-trend',
    label: 'Subscription movement',
    hint: 'Started, moved, cancelled per bucket',
  },
];

/**
 * What the platform earned over a period, and what happened to the paying base.
 *
 * Deliberately not the same screen as Billing. Billing is a snapshot of what is
 * owed *now* and a place to act on it; this is what happened over a window and
 * has no buttons at all. Neither is the other one summed up, and putting them
 * together would invite reading one as the other.
 */
export function AdminRevenueReportPage() {
  const [period, setPeriod] = useState(90);

  const {
    data: revenue,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['platform-analytics', 'revenue', period],
    queryFn: () => analyticsApi.getRevenueReport(period),
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['platform-analytics', 'subscriptions', period],
    queryFn: () => analyticsApi.getSubscriptionReport(period),
  });

  const subscriptions = subscriptionsQuery.data;

  if (isPending) return <LoadingState label="Adding up the invoices…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { totals, currency } = revenue;
  const money = (cents) => formatMoney(cents, currency);
  const churn = subscriptions?.churn;

  return (
    <div>
      <PageHeader
        title="Revenue"
        description="What was billed and what arrived, over the period you choose."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker value={period} onChange={setPeriod} />
            <ExportMenu sheets={EXPORT_SHEETS} days={period} />
          </div>
        }
      />

      {revenue.mixed_currency && (
        <Alert tone="warning" title="These totals span more than one currency" className="mb-6">
          Every figure below is a sum of minor units, so with invoices in different currencies the
          totals are not money. Read the by-plan table instead.
        </Alert>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Billed"
          value={money(totals.billed_cents)}
          hint={`${count(totals.invoices_issued)} invoices issued`}
        />
        <StatTile
          label="Collected"
          value={money(totals.collected_cents)}
          hint={
            totals.median_days_to_payment == null
              ? 'Nothing paid in this period'
              : `Typically ${days(totals.median_days_to_payment)} after issue`
          }
        />
        <StatTile
          label="Outstanding"
          value={money(totals.outstanding_cents)}
          // Not windowed, and saying so matters: it is what is owed today, not
          // what went unpaid out of this period's invoices.
          hint={
            totals.overdue_cents > 0
              ? `${money(totals.overdue_cents)} of it overdue`
              : 'Owed right now, nothing overdue'
          }
        />
        <StatTile
          label="Discounted"
          value={money(totals.discount_cents)}
          hint={`${count(revenue.redemptions)} redemptions · ${count(revenue.live_coupons)} codes live`}
        />
      </div>

      <Card className="mb-6">
        <CardHeader
          title="Money over time"
          description="Billed on the date the invoice was issued; collected on the date it was paid. The gap between the two lines is what is still owed."
        />
        <TrendChart
          points={revenue.trend}
          series={REVENUE_SERIES}
          granularity={revenue.period.granularity}
          formatValue={money}
          emptyLabel="Nothing was billed in this period"
        />
      </Card>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="By plan"
            description="Grouped by the plan named on each invoice, so a company that upgraded mid-period appears under both."
          />
          <ReportTable
            rows={revenue.by_plan}
            rowKey={(row) => row.plan_code}
            empty="No invoices in this period"
            columns={[
              {
                key: 'plan_code',
                header: 'Plan',
                // A one-off invoice belongs to no plan, and that placeholder is
                // what keeps it out of subscription revenue everywhere else.
                cell: (row) => (row.plan_code === 'one-off' ? 'One-off billing' : row.plan_code),
              },
              {
                key: 'invoices',
                header: 'Invoices',
                numeric: true,
                cell: (row) => count(row.invoices),
              },
              {
                key: 'billed_cents',
                header: 'Billed',
                numeric: true,
                cell: (row) => money(row.billed_cents),
              },
              {
                key: 'collected_cents',
                header: 'Collected',
                numeric: true,
                cell: (row) => money(row.collected_cents),
              },
            ]}
          />
          <p className="text-ink-muted mt-3 text-xs">
            Recurring {money(totals.subscription_cents)} · one-off {money(totals.one_off_cents)}
            {totals.voided_cents > 0 && ` · ${money(totals.voided_cents)} voided and excluded`}
          </p>
        </Card>

        <Card>
          <CardHeader
            title="What discounts cost"
            description="Read from the invoices each code came off, not from how often it was used."
          />
          <ReportTable
            rows={revenue.by_coupon}
            rowKey={(row) => row.coupon_code}
            empty="No discount codes were used"
            columns={[
              {
                key: 'coupon_code',
                header: 'Code',
                cell: (row) => <span className="font-mono text-xs">{row.coupon_code}</span>,
              },
              {
                key: 'invoices',
                header: 'Used',
                numeric: true,
                cell: (row) => count(row.invoices),
              },
              {
                key: 'discount_cents',
                header: 'Given away',
                numeric: true,
                cell: (row) => money(row.discount_cents),
              },
              {
                key: 'billed_cents',
                header: 'Still billed',
                numeric: true,
                cell: (row) => money(row.billed_cents),
              },
            ]}
          />
        </Card>
      </div>

      {/* The paying-base half is its own query, so it loads and fails on its
          own without taking the revenue figures above down with it. */}
      {subscriptionsQuery.isPending && (
        <div className="space-y-6" role="status" aria-label="Loading the paying base">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-20" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      )}

      {subscriptionsQuery.isError && (
        <Alert tone="error" title="Could not load the paying base">
          <p>{errorMessage(subscriptionsQuery.error)}</p>
          <Button
            variant="secondary"
            size="xs"
            className="mt-2"
            onClick={() => void subscriptionsQuery.refetch()}
          >
            Try again
          </Button>
        </Alert>
      )}

      {subscriptions && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Paying now"
              value={count(subscriptions.standing.entitled)}
              hint={
                subscriptions.standing.leaving > 0
                  ? `${count(subscriptions.standing.leaving)} leaving at period end`
                  : 'Nobody on their way out'
              }
            />
            <StatTile
              label="Net new"
              value={count(subscriptions.net_new)}
              hint="Started minus cancelled. Plan moves are not counted."
            />
            <StatTile
              label="Churn"
              value={percent(churn?.churn_rate)}
              // The definition travels with the number, because the several
              // reasonable definitions of churn disagree enough to matter.
              hint={`${count(churn?.cancellations)} of ${count(
                (churn?.retained ?? 0) + (churn?.cancellations ?? 0),
              )} who could have left`}
            />
            <StatTile
              label="On a discount"
              value={count(subscriptions.standing.on_coupon)}
              hint="Subscriptions still carrying a code"
            />
          </div>

          <Card>
            <CardHeader
              title="Movement in the paying base"
              description="An upgrade starts and bills at once; a downgrade waits for the period already paid for. Resumed cancellations are counted but not plotted."
            />
            <TrendChart
              points={subscriptions.trend}
              series={SUBSCRIPTION_SERIES}
              granularity={subscriptions.period.granularity}
              emptyLabel="Nobody joined or left in this period"
            />
          </Card>
        </>
      )}
    </div>
  );
}
